import type { Database } from 'node-sqlite3-wasm';

export function getParentFileMapHash(
  db: Database,
  parentId: number,
  filePath: string,
): string | null | undefined {
  const row = db.get(
    'SELECT content_hash FROM symbol_parent_file_map WHERE parent_id = ? AND file_path = ?',
    [parentId, filePath],
  ) as { content_hash: string | null } | null;
  if (row == null) return undefined;
  return row.content_hash;
}

export function countParentFileMapRefs(db: Database, contentHash: string): number {
  const row = db.get('SELECT COUNT(*) as n FROM symbol_parent_file_map WHERE content_hash = ?', [
    contentHash,
  ]) as { n: number | bigint };
  return Number(row.n);
}

export function setParentFileMapEntry(
  db: Database,
  parentId: number,
  filePath: string,
  contentHash: string | null,
): string | null {
  const previous = getParentFileMapHash(db, parentId, filePath);
  if (contentHash == null) {
    db.run(
      `INSERT INTO symbol_parent_file_map (parent_id, file_path, content_hash)
       VALUES (?, ?, NULL)
       ON CONFLICT(parent_id, file_path) DO UPDATE SET content_hash = NULL`,
      [parentId, filePath],
    );
  } else {
    db.run(
      `INSERT INTO symbol_parent_file_map (parent_id, file_path, content_hash)
       VALUES (?, ?, ?)
       ON CONFLICT(parent_id, file_path) DO UPDATE SET content_hash = excluded.content_hash`,
      [parentId, filePath, contentHash],
    );
  }
  return previous === undefined ? null : previous;
}

export function removeParentFileMapEntry(db: Database, parentId: number, filePath: string): string | null {
  const previous = getParentFileMapHash(db, parentId, filePath);
  db.run('DELETE FROM symbol_parent_file_map WHERE parent_id = ? AND file_path = ?', [parentId, filePath]);
  return previous === undefined ? null : previous;
}

export function clearParentFileMap(db: Database, parentId: number): string[] {
  const rows = db.all('SELECT content_hash FROM symbol_parent_file_map WHERE parent_id = ? AND content_hash IS NOT NULL', [
    parentId,
  ]) as Array<{ content_hash: string }>;
  db.run('DELETE FROM symbol_parent_file_map WHERE parent_id = ?', [parentId]);
  return rows.map((row) => row.content_hash);
}

export function clearParentFileMapBatch(
  db: Database,
  parentId: number,
  limit: number,
): { released: string[]; remaining: boolean } {
  const rows = db.all(
    `SELECT file_path, content_hash
     FROM symbol_parent_file_map
     WHERE parent_id = ?
     LIMIT ?`,
    [parentId, limit],
  ) as Array<{ file_path: string; content_hash: string | null }>;
  if (rows.length === 0) return { released: [], remaining: false };

  const del = db.prepare('DELETE FROM symbol_parent_file_map WHERE parent_id = ? AND file_path = ?');
  try {
    for (const row of rows) {
      del.run([parentId, row.file_path]);
    }
  } finally {
    del.finalize();
  }

  return {
    released: rows.flatMap((row) => (row.content_hash == null ? [] : [row.content_hash])),
    remaining: rows.length >= limit,
  };
}
